import { ChevronDown } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { cn } from '@/lib/utils'
import type { OrganizationBillingState } from '@/gen/cortado/v1/subscriptions_pb'
import {
  applyLiveCheckoutTotals,
  buildOrderSummary,
  type CheckoutSelection,
  type LiveCheckoutTotals,
} from './order-summary-model'

interface OrderSummaryProps {
  billingState: OrganizationBillingState
  selection: CheckoutSelection | null
  // Page density collapses the summary into a disclosure bar below `lg`.
  // Compact density renders the static block at every width.
  collapsible?: boolean
  // Totals published by the live Checkout Session once the payment step is
  // mounted. Null before then, and the quoted figures stand.
  liveTotals?: LiveCheckoutTotals | null
}

/**
 * Persistent order summary beside the checkout steps. Every amount and date is
 * the server's quote rendered verbatim - the only computed figure is the
 * per-month equivalent from `perMonthEquivalentCents`.
 */
export function OrderSummary({
  billingState,
  selection,
  collapsible = false,
  liveTotals = null,
}: OrderSummaryProps) {
  const summary = selection
    ? applyLiveCheckoutTotals(buildOrderSummary(billingState, selection), liveTotals)
    : null

  const body = (
    <div className="space-y-4 rounded-xl border bg-card p-5">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold">Order summary</h2>
        {summary ? (
          <Badge variant={summary.trialAvailable ? 'success' : 'secondary'}>{summary.trialLabel}</Badge>
        ) : null}
      </div>

      {!summary ? (
        <p className="text-sm text-muted-foreground">Pick a billing interval to see your total.</p>
      ) : (
        <>
          <div className="space-y-1">
            <p className="font-medium">
              {summary.planLabel} · {summary.intervalLabel}
            </p>
            <p className="text-xl font-bold">{summary.termPriceFormatted}</p>
            {summary.taxNote ? (
              <p className="text-xs text-muted-foreground">{summary.taxNote}</p>
            ) : null}
            {summary.perMonthEquivalentLabel ? (
              <p className="text-xs text-muted-foreground">{summary.perMonthEquivalentLabel}</p>
            ) : null}
          </div>

          {summary.lines.length > 0 ? (
            <dl className="space-y-1 border-t pt-3 text-sm">
              {summary.lines.map((line) => (
                <div key={line.label} className="flex items-center justify-between gap-4">
                  <dt className="text-muted-foreground">{line.label}</dt>
                  <dd className="font-medium">{line.value}</dd>
                </div>
              ))}
            </dl>
          ) : null}

          {summary.breakdown ? (
            <p className="text-xs text-muted-foreground">{summary.breakdown}</p>
          ) : null}

          <div className="flex items-center justify-between gap-4 border-t pt-3">
            <span className="text-sm font-semibold">Due today</span>
            <span className="text-lg font-bold">{summary.dueTodayFormatted}</span>
          </div>

          <p className="text-xs text-muted-foreground">
            First charge {summary.firstChargeAmountLabel} on {summary.firstChargeDateLabel}.
          </p>
          <p className="text-xs text-muted-foreground">Cancel anytime.</p>
        </>
      )}
    </div>
  )

  if (!collapsible) return body

  return (
    <Collapsible>
      <CollapsibleTrigger
        className={cn(
          'group flex w-full items-center justify-between gap-2 rounded-xl border bg-card px-5 py-3 text-sm font-semibold lg:hidden'
        )}
      >
        <span>Order summary{summary ? ` · ${summary.dueTodayFormatted} due today` : ''}</span>
        <ChevronDown className="h-4 w-4 transition-transform group-data-[state=open]:rotate-180" />
      </CollapsibleTrigger>
      <CollapsibleContent
        forceMount
        className="mt-2 data-[state=closed]:hidden lg:mt-0 lg:data-[state=closed]:block"
      >
        {body}
      </CollapsibleContent>
    </Collapsible>
  )
}
